<template>
  <div class="discount-campaigns">
    <teleport to="body">
      <overlay
        key="discountCampaignsActivatedModal"
        block-class="discount-campaigns-overlay"
        namespace="discountCampaignsActivatedModal"
        direction="up"
        :show-close="false"
        type="modal"
      >
        <template #body>
          <div class="discount-campaigns__header">
            <button
              class="discount-campaigns__close icon-button"
              type="button"
              @click="closeOverlay('discountCampaignsActivatedModal')"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.close')"
              />

              <icon-close />
            </button>

            <p
              class="discount-campaigns__title text-mobile-headlines-h4 text-desktop-headlines-h4"
              v-text="$string('discount_campaigns.activated_modal.title')"
            />

            <span
              class="discount-campaigns__code text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
              v-text="code"
            />
          </div>

          <div class="discount-campaigns__content">
            <p
              class="text-center text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
              v-html="
                $string('discount_campaigns.activated_modal.content_html')
              "
            />
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Discount campaigns (discount-campaigns)
 * -----------------------------------------------------------------------------
 * Global component used to query and apply discount campaigns.
 * - JS Cookie import is found otherwise cookie functionality wouldn't work
 *
 */
// eslint-disable-next-line import/no-unresolved
import Cookies from 'js-cookie';
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useIndexStore from '~stores/index';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import Overlay from '~global/overlay/overlay.vue';
import IconClose from '~icons/directional-navigation/close.svg';
import { dateTime } from '~/helpers/general';

export default {
  name: 'DiscountCampaigns',

  components: {
    IconClose,
    Overlay,
  },

  data() {
    return {
      campaigns: [],
      cnvsGlobal: true,
      influencers: [],
      privateCampaigns: [],
      localActiveCampaign: false,
      localCode: '',
      locationHost: location?.host?.replace('www.', ''),
    };
  },

  computed: {
    ...mapState(useIndexStore, { hydrated: 'hydrated' }),
    ...mapState(useCartStore, { cart: 'response' }),
    ...mapState(useDiscountCampaignsStore, {
      activeCampaign: 'active',
      code: 'code',
      urlDiscount: 'urlDiscount',
      codeState: 'codeState',
      validNativeCode: 'validNativeCode',
    }),

    /**
     * Compute promoted campaign based.
     * - Promoted campaign must be 'discount' type, have valid dates, and if it
     *   has markets then the current market must match.
     * - Returns nothing when active campaign is set.
     * @returns {Boolean|Object}
     */
    localPromotedCampaign() {
      if (this.localActiveCampaign || this.activeCampaign) {
        return false;
      }

      return this.campaigns?.find((campaign) => {
        if (campaign.type !== 'discount' || !campaign.promote) {
          return false;
        }

        return (
          this.getValidCampaignDate(campaign) &&
          this.getValidCampaignMarket(campaign)
        );
      });
    },

    /**
     * Compute array of valid campaign codes.
     * @returns {Array}
     */
    validCampaignCodes() {
      return this.campaigns
        .filter((campaign) => campaign.type !== 'influencer')
        .map((campaign) => campaign.code.map((code) => code.toUpperCase()))
        .flat();
    },

    /**
     * Compute array of valid influencer codes.
     * @returns {Array}
     */
    validInfluencerCodes() {
      return this.influencers.map((code) => code.toUpperCase());
    },

    validPrivateCodes() {
      return this.privateCampaigns
        .map((campaign) => campaign.code.toUpperCase())
        .flat();
    },

    codeType() {
      const campaignIsInfluencer =
        this.localActiveCampaign?.type === 'influencer';

      if (this.validPrivateCodes.includes(this.code)) {
        return 'priv';
      }

      if (this.isNativeCode || !campaignIsInfluencer) {
        return 'pub';
      }

      return 'infl';
    },

    /**
     * Compute if code is native code.
     * @returns {boolean}
     */
    isNativeCode() {
      return (
        this.validNativeCode === this.localCode &&
        this.validNativeCode !== '' &&
        !this.validCampaignCodes.includes(this.localCode) &&
        !this.validInfluencerCodes.includes(this.localCode)
      );
    },
  },

  watch: {
    /**
     * Watch cart discount codes for discount/URL updates.
     */
    'cart.discount_codes'() {
      this.setDiscountCodeFromCart();
    },

    /**
     * Watch code Pinia state to run query when it changes.
     */
    code() {
      if (!this.code) {
        this.clearCode('ready');
        return;
      }

      if (this.code === this.localCode) {
        return;
      }

      this.determineValidCode();
    },

    /**
     * Watch local active campaign to update Pinia state.
     * - Don't immediately execute as mounted functionality needs to check if
     *   it's valid.
     */
    localActiveCampaign() {
      if (
        this.localActiveCampaign &&
        this.localActiveCampaign === this.activeCampaign
      ) {
        return;
      }

      this.setCampaign({ campaign: this.localActiveCampaign, type: 'active' });

      cnvs.EventBus.emit('discount-campaign', {
        active: this.localActiveCampaign,
        promoted: this.localPromotedCampaign,
      });
    },

    /**
     * Watch local promoted campaign to update Pinia state.
     */
    localPromotedCampaign: {
      handler() {
        this.setCampaign({
          campaign: this.localPromotedCampaign,
          type: 'promoted',
        });

        cnvs.EventBus.emit('discount-campaign', {
          active: this.localActiveCampaign,
          promoted: this.localPromotedCampaign,
        });
      },
      immediate: true,
    },

    async hydrated() {
      if (this.hydrated) {
        await this.$nextTick();
        /**
         * Update local state.
         */
        this.campaigns = this.$variable('discount.campaigns');
        this.influencers = this.$variable('discount.influencers');
        this.privateCampaigns = this.$variable('discount.privateCampaigns');

        /**
         * Clear code if code is invalid.
         * - Fallback reset is stuck in loading state on page load.
         */
        if (['loading', 'invalid'].includes(this.codeState)) {
          this.clearCode('ready');
        }

        /**
         * Clear code with error if expired or invalid market.
         */
        if (this.activeCampaign) {
          this.localActiveCampaign = this.activeCampaign;
          /**
           * Verify code again to check for updated discount config from metafield.
           * The config check is implicitly done during the code verification.
           * this.activeCampaign at this point could also been read from localStorage Pinia cache
           */
          if (this.code !== '') {
            await this.determineValidCode();
          }

          const validDate = this.getValidCampaignDate(this.activeCampaign);
          const validMarket = this.getValidCampaignMarket(this.activeCampaign);

          if (!validDate || !validMarket) {
            const errorType = validDate ? 'market' : 'expired';

            this.setCode('');

            /**
             * Arbitrary wait to override set code clear code state.
             */
            await cnvs.Sleep(500);
            this.setCodeState('invalid');
            this.setErrorType(errorType);
            return;
          }
        }

        /**
         * Set discount code from cart Ajax API or custom cookie.
         */
        this.setDiscountCodeFromCart();
        this.setDiscountCodeFromCustomCookie();

        /**
         * If we have an active campaign, check if freebies are the same with the
         * potentionally updated campaign
         */
        if (this.activeCampaign) {
          this.checkFreebiesState();
        }
      }
    },
  },

  methods: {
    ...mapActions(useDiscountCampaignsStore, {
      setCampaign: 'setCampaign',
      setCode: 'setCode',
      setCodeState: 'setCodeState',
      setUrlDiscount: 'setUrlDiscount',
      setErrorType: 'setErrorType',
      setCartItemPrices: 'setCartItemPrices',
      verifyDiscountCodeIsApplicable: 'verifyDiscountCodeIsApplicable',
    }),
    ...mapActions(useCartStore, {
      updateCartAttributes: 'updateCartAttributes',
    }),
    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    /**
     * Check if the freebies are the same as the ones in the metafield of the campaign
     * and sets the metafield freebies to the ones in the active campaign.
     */
    checkFreebiesState() {
      const metafieldActiveCampaign = this.campaigns.filter(
        (campaign) => campaign.campaignId === this.activeCampaign.campaignId,
      )[0];

      if (!metafieldActiveCampaign) {
        return;
      }

      // turning them into json to properly compare them as they always
      // should have the same structure and order of properties
      const freebiesAreDifferent =
        JSON.stringify(metafieldActiveCampaign?.freeProducts) !==
        JSON.stringify(this.activeCampaign?.freeProducts);

      if (freebiesAreDifferent) {
        this.activeCampaign.freeProducts =
          metafieldActiveCampaign?.freeProducts;
      }
    },

    /**
     * Set discount code based on cart Ajax API.
     */
    setDiscountCodeFromCart() {
      if (['loading', 'invalid'].includes(this.codeState)) {
        return;
      }

      if (!this.cart) {
        return;
      }

      const cartDiscountCode = this.cart.discount_codes?.length
        ? this.cart.discount_codes[0]?.code?.toUpperCase()
        : '';

      /**
       * If cart discount code is empty but a code exists then assume the code
       * has been cleared at checkout.
       */
      if (!cartDiscountCode && (this.localCode || this.code)) {
        this.setCode('');
        return;
      }

      /**
       * Don't set if it matches the current code.
       */
      if (
        this.localCode === cartDiscountCode ||
        this.code === cartDiscountCode
      ) {
        return;
      }
      this.setCode(cartDiscountCode);
    },

    /**
     * Set discount code based on custom cookies.
     * - Applies discount code when already applied to another sub-domain.
     */
    setDiscountCodeFromCustomCookie() {
      const changedBy = Cookies.get(
        this.$variable('settings.discounts.changedByCookie'),
      );
      const changeTo = Cookies.get(
        this.$variable('settings.discounts.changeToCookie'),
      )?.toUpperCase();

      if (
        !changedBy ||
        !changeTo ||
        changedBy === this.locationHost ||
        changeTo === this.code
      ) {
        return;
      }

      this.setCode(changeTo?.toUpperCase());
    },

    /**
     * Clear discount code, active campaign, and cookies.
     * @param {String} state - Code state to set.
     * @param {String} [errorType] - If state is `invalid` then provide error.
     */
    async clearCode(state, errorType) {
      try {
        if (state) {
          this.setCodeState(state);
        }

        if (errorType) {
          this.setErrorType(errorType);
        }

        this.localActiveCampaign = false;
        await this.setCartAttributes(true);
        const cartStore = useCartStore();
        await cartStore.checkRemoveFreebies();
        this.setCartItemPrices([{}]);
        this.clearCookies();
        this.localCode = '';
      } catch (error) {
        cnvs.ReportError('Failed to clear code', error);
      }
    },

    /**
     * Determine if the code is valid and set campaigns if valid one exists.
     */
    async determineValidCode() {
      this.setCodeState('loading');

      try {
        this.localCode = this.code;

        /**
         * Set discount campaign if valid campaign exists.
         */
        if (this.validCampaignCodes.includes(this.localCode)) {
          const discountCampaign = this.getValidDiscountCampaign();

          if (discountCampaign) {
            if (discountCampaign.error) {
              this.clearCode('invalid', discountCampaign.error);
              return;
            }

            this.setActiveCampaign(discountCampaign);
            return;
          }
        }

        /**
         * Set influencer campaign if valid campaign exists.
         */
        if (this.validInfluencerCodes.includes(this.localCode)) {
          const influencerCampaign = this.getValidInfluencerCampaign();

          if (influencerCampaign) {
            this.setActiveCampaign(influencerCampaign);
            return;
          }
        }

        if (this.validPrivateCodes.includes(this.localCode)) {
          const privateCampaign = this.getValidPrivateCampaign();

          if (privateCampaign && !privateCampaign.error) {
            this.setActiveCampaign(privateCampaign);
            return;
          }
        }

        // if it's not in the valid codes, it may be a native discount code.
        try {
          await this.verifyDiscountCodeIsApplicable(this.code);
          if (this.isNativeCode) {
            this.localActiveCampaign = false;
            await this.setCartAttributes();
            this.setCodeState('valid');
            this.setCookies();
            return;
          }
        } catch (verifyError) {
          console.error('Error verifying native code:', verifyError);
          // Continue with invalid code handling instead of failing completely
        }

        this.clearCode('invalid', 'invalid');
      } catch (error) {
        console.error('Error in determineValidCode:', error);
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
        this.clearCode('error', error.message);
      }
    },

    /**
     * Get and set active campaign.
     * - Find campaigns with matching code and market.
     * - There must be a matching code because determineValidCampaign() is only
     *   called when the code is valid.
     * @returns {Object}
     */
    getValidDiscountCampaign() {
      let matches = this.campaigns?.filter((campaign) => {
        return (
          campaign.type === 'discount' && campaign.code.includes(this.code)
        );
      });

      /**
       * If no matches there must be an internal error.
       */
      if (!matches.length) {
        return { error: 'internal' };
      }

      /**
       * Filter valid markets.
       */
      matches = matches.filter(this.getValidCampaignMarket);

      if (!matches.length) {
        return { error: 'market' };
      }

      /**
       * Filter valid dates.
       */
      matches = matches.filter(this.getValidCampaignDate);

      if (!matches.length) {
        return { error: 'expired' };
      }

      /**
       * Return first valid campaign.
       */
      return matches[0];
    },

    /**
     * Determine details and set active campaign.
     * @param {Object} campaign - Campaign to make active.
     */
    async setActiveCampaign(campaign) {
      const discountCampaignsStore = useDiscountCampaignsStore();

      /**
       * Determine best discount in campaign.
       * Include both regular discounts and graduated discount thresholds.
       */
      const regularDiscounts = campaign.discounts.map(
        (entry) => entry.discount,
      );

      const graduatedDiscounts = campaign.graduatedDiscounts
        ? campaign.graduatedDiscounts.map((entry) => entry.threshold)
        : [];

      // Combine all discount values
      const allDiscounts = [...regularDiscounts, ...graduatedDiscounts];

      const discounts = allDiscounts.sort((a, b) => a - b).reverse();

      /**
       * Set active campaign.
       * - Add code as this is missing from influencer type campaigns.
       */

      this.localActiveCampaign = {
        ...campaign,
        code: campaign.code ? campaign.code : [this.localCode],
        maxDiscount: discounts[0],
      };

      /**
       * Set attributes and statuses.
       */

      await this.setCartAttributes();
      discountCampaignsStore.refreshCampaignThresholds();
      await discountCampaignsStore.loadFreeProducts();

      const cartStore = useCartStore();
      await cartStore.checkAddFreebies();
      await cartStore.checkRemoveFreebies();

      // freebie check

      this.setCodeState('valid');
      this.setCookies();

      /**
       * If discount is set from URL then open success modal.
       * Also prevent campaign popup from stacking with the success overlay.
       */
      if (!this.urlDiscount || this.$variable('request.pageType') === 'cart') {
        return;
      }

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'discountCampaignsActivatedModal',
        clearQueue: true,
      });
      await this.determineValidCode();

      this.setUrlDiscount(false);
    },

    /**
     * Get valid campaign date.
     * - Determine if current date is valid for active campaign.
     * @param {Object} campaign - Campaign to test.
     * @returns {Boolean}
     */
    getValidCampaignDate(campaign) {
      if (!campaign) {
        return false;
      }

      try {
        // Validate dates before passing to dateTime
        const startDate = campaign?.startDate;
        const endDate = campaign?.endDate;

        // If startDate is missing or invalid, return false
        if (!startDate) {
          console.warn('Missing start date:', startDate);
          return false;
        }

        // Special handling for 'false' start date (shouldn't happen but let's be safe)
        if (startDate === 'false') {
          console.warn('Start date is "false", treating as valid');
        } else {
          // Validate startDate is a valid date string
          const parsedStartDate = new Date(startDate);
          if (isNaN(parsedStartDate.getTime())) {
            console.warn('Invalid start date:', startDate);
            return false;
          }
        }

        // Special handling for 'false' end date
        if (endDate === 'false') {
          return dateTime(startDate, 'false');
        }

        // If endDate exists and is not 'false', validate it
        if (endDate && endDate !== 'false') {
          const parsedEndDate = new Date(endDate);
          if (isNaN(parsedEndDate.getTime())) {
            console.warn('Invalid end date:', endDate);
            return false;
          }
        }

        return dateTime(startDate, endDate);
      } catch (error) {
        console.error('Error validating campaign dates:', error);
        return false;
      }
    },

    /**
     * Get valid campaign market.
     * - Determine if current market is valid for active campaign.
     * @param {Object} campaign - Campaign to test.
     * @returns {Boolean}
     */
    getValidCampaignMarket(campaign) {
      if (!campaign) {
        return false;
      }

      const currentMarket = this.$variable('store.market');
      const isValid = campaign?.markets?.length
        ? campaign?.markets.some((market) => {
            return market?.toLowerCase() === currentMarket;
          })
        : true;

      return isValid;
    },

    /**
     * Get valid influencer campaign.
     * @returns {Object|Boolean}
     */
    getValidInfluencerCampaign() {
      return this.campaigns?.find((campaign) => {
        return (
          campaign.type === 'influencer' &&
          this.getValidCampaignDate(campaign) &&
          this.getValidCampaignMarket(campaign)
        );
      });
    },

    /**
     * Get valid private campaign.
     * @returns {Object|Boolean}
     */
    getValidPrivateCampaign() {
      let matches = this.privateCampaigns?.filter((campaign) => {
        return campaign.code.toUpperCase() === this.code;
      });

      /**
       * If no matches there must be an internal error.
       */
      if (!matches.length) {
        return { error: 'internal' };
      }

      /**
       * Filter valid markets.
       */
      matches = matches.filter(this.getValidCampaignMarket);

      if (!matches.length) {
        return { error: 'market' };
      }

      /**
       * Filter valid dates.
       */
      matches = matches.filter(this.getValidCampaignDate);

      if (!matches.length) {
        return { error: 'expired' };
      }

      /**
       * Return first valid campaign.
       */
      return matches[0];
    },

    /**
     * Set cart attributes and discount.
     * - Currently there is no validation so use custom validation.
     * @param {Boolean} clear - Clearing code?
     * @returns {Promise}
     */
    setCartAttributes(clear) {
      /* eslint-disable camelcase */
      return new Promise(async (resolve, reject) => {
        try {
          let discount_codes = `${this.code?.toLowerCase()}:${this.codeType}`;
          let discount = this.code?.toLowerCase();

          if (clear) {
            discount_codes = null;
            discount = '';
          }

          const payload = {
            attributes: { discount_codes },
            discount,
          };

          const response = await this.updateCartAttributes(payload);

          resolve(response);
        } catch (error) {
          cnvs.ReportError('Failed to set cart attributes', error);
          reject(error);
        }
      });
      /* eslint-enable camelcase */
    },

    /**
     * Set custom cookies.
     * - Create custom cookies used by other ESN stores to set the active
     *   discount code when switching sub-domains.
     * - Cookie names and values come from 1.0 codebase.
     * @returns {Promise}
     */
    setCookies() {
      return new Promise(async (resolve, reject) => {
        try {
          const config = this.getCookieConfig();
          await Cookies.set(
            this.$variable('settings.discounts.changedByCookie'),
            this.locationHost,
            config,
          );
          await Cookies.set(
            this.$variable('settings.discounts.changeToCookie'),
            this.code,
            config,
          );
          resolve();
        } catch (error) {
          cnvs.ReportError('Failed to set custom cookie', error);
          reject(error);
        }
      });
    },

    /**
     * Get cookie config.
     * @returns {Object}
     */
    getCookieConfig() {
      return {
        domain: this.$variable('settings.discounts.discountCookieDomain'),
      };
    },

    /**
     * Clear custom cookies.
     * - 1.0 stores don't clear active discount code from other stores when
     *   clearing discount on a store.
     * @returns {Promise}
     */
    clearCookies() {
      return new Promise(async (resolve, reject) => {
        try {
          const config = this.getCookieConfig();
          await Cookies.remove(
            this.$variable('settings.discounts.changedByCookie'),
            config,
          );
          await Cookies.remove(
            this.$variable('settings.discounts.changeToCookie'),
            config,
          );
          resolve();
        } catch (error) {
          cnvs.ReportError('Failed to clear cookies', error);
          reject(error);
        }
      });
    },

    /**
     * Verifies if discount code is applicable to cart.
     * @returns {Promise}
     */
    verifyDiscountCodeIsApplicable(code) {
      return new Promise(async (resolve, reject) => {
        try {
          const cartStore = useCartStore();
          const discount = code === '' ? this.code : code?.toLowerCase();

          if (discount === '') {
            resolve();
            return;
          }

          const payload = {
            discount,
          };

          const response = await cartStore.updateCartAttributes(payload);

          // Check if response and discount_codes exist before accessing
          let isApplicable = false;
          if (
            response &&
            response.discount_codes &&
            response.discount_codes.length > 0
          ) {
            isApplicable = response.discount_codes[0]?.applicable || false;
          }

          // Only set cart item prices if response.items exists
          if (response && response.items) {
            await this.setCartItemPrices(response.items);
          }

          this.validNativeCode = isApplicable ? code : '';

          resolve(response);
        } catch (error) {
          cnvs.ReportError('Failed to set cart attributes', error);
          reject(error);
        }
      });
    },
  },
};
</script>
